import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { ScannerPairingChallenge, ScannerPairingResult } from '../models/qr-scanner.models';

/**
 * Pareamento do celular com uma sessão de scanner, sem login.
 *
 * O celular troca um código de pareamento de uso único por um token de
 * capacidade. Esse token só vale no hub do scanner e só para aquela sessão —
 * não é credencial de conta e não abre nada na API.
 *
 * Usa `fetch` direto, sem passar pelos interceptors: este é um fluxo anônimo
 * e não deve herdar comportamento de token nem redirecionamento para login.
 */
@Injectable({
  providedIn: 'root'
})
export class ScannerPairingService {
  private readonly TOKEN_KEY = 'scanner-token';
  private readonly SESSION_KEY = 'scanner-session-id';
  private readonly DEVICE_ID_KEY = 'scanner-device-id';
  private readonly DEVICE_LABEL_KEY = 'scanner-device-label';

  /**
   * Identificador do aparelho, gerado uma vez e guardado localmente.
   * Amarra o token ao celular que pareou.
   */
  getDeviceId(): string {
    let deviceId = this.read(this.DEVICE_ID_KEY);

    if (!deviceId) {
      deviceId = this.randomId();
      this.write(this.DEVICE_ID_KEY, deviceId);
    }

    return deviceId;
  }

  getDeviceLabel(): string {
    return this.read(this.DEVICE_LABEL_KEY) || 'Celular';
  }

  setDeviceLabel(label: string): void {
    this.write(this.DEVICE_LABEL_KEY, label);
  }

  getToken(): string | null {
    return this.read(this.TOKEN_KEY);
  }

  getSessionId(): string | null {
    return this.read(this.SESSION_KEY);
  }

  clear(): void {
    this.remove(this.TOKEN_KEY);
    this.remove(this.SESSION_KEY);
  }

  /**
   * Extrai o código de pareamento do conteúdo do QR.
   *
   * O QR carrega a URL da tela do scanner com o código no **fragmento**
   * (`#c=...`), que nunca é enviado ao servidor nem entra em log de acesso.
   * Aceita também o código puro, para leitura feita dentro do app.
   */
  extractPairingCode(scanned: string): string | null {
    const text = (scanned || '').trim();
    if (!text) return null;

    const fromFragment = text.match(/[#&]c=([A-Za-z0-9_-]+)/);
    if (fromFragment) return fromFragment[1];

    // Não é URL: trata como o próprio código.
    if (!/^https?:\/\//i.test(text) && /^[A-Za-z0-9_-]{20,}$/.test(text)) {
      return text;
    }

    return null;
  }

  /** Lê e apaga o código do endereço, para não ficar no histórico do navegador. */
  consumeCodeFromUrl(): string | null {
    if (typeof window === 'undefined') return null;

    const code = this.extractPairingCode(window.location.hash);
    if (code) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    return code;
  }

  /**
   * Passo 1: apresenta o código lido do QR e recebe os dígitos de confirmação.
   * **Não pareia nada** — sem a digitação do operador no desktop, isto não
   * vira acesso.
   */
  async requestPairing(code: string, deviceLabel?: string): Promise<ScannerPairingChallenge> {
    const label = deviceLabel || this.getDeviceLabel();

    const response = await fetch(`${environment.apiUrl}/scanner/pair`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        code,
        deviceId: this.getDeviceId(),
        deviceLabel: label
      })
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new Error(body?.description || 'Não foi possível falar com a estação.');
    }

    this.setDeviceLabel(label);
    return response.json();
  }

  /**
   * Passo 2: espera o operador confirmar no desktop.
   *
   * 202 = ainda aguardando; 200 = confirmado, com o token; 400 = a tentativa
   * expirou ou foi descartada (por exemplo, porque o operador confirmou outro
   * aparelho).
   */
  async waitForConfirmation(
    attemptId: string,
    expiresAt: string,
    onTick?: (secondsLeft: number) => void
  ): Promise<ScannerPairingResult> {
    const deadline = new Date(expiresAt).getTime();

    while (Date.now() < deadline) {
      const response = await fetch(`${environment.apiUrl}/scanner/pair/${encodeURIComponent(attemptId)}`);

      if (response.status === 200) {
        const result: ScannerPairingResult = await response.json();
        this.write(this.TOKEN_KEY, result.token);
        this.write(this.SESSION_KEY, result.sessionId);
        return result;
      }

      if (response.status !== 202) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.description || 'O pareamento não foi confirmado.');
      }

      onTick?.(Math.max(0, Math.round((deadline - Date.now()) / 1000)));
      await new Promise(resolve => setTimeout(resolve, 2000));
    }

    throw new Error('Tempo esgotado. Leia o QR Code da estação novamente.');
  }

  private randomId(): string {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  }

  private read(key: string): string | null {
    try {
      return typeof localStorage === 'undefined' ? null : localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private write(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Navegador sem armazenamento (aba privada): o pareamento ainda vale
      // nesta sessão, só não sobrevive a um recarregamento.
    }
  }

  private remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignorado pelo mesmo motivo acima
    }
  }
}
